import { Injectable, inject, signal } from "@angular/core";
import { BehaviorSubject, interval, Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { HomecardsService } from "@core/AuthServices/homeCards/homecards.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { VoucherClaimResult } from "@interfaces/vouchers.i";
import { ClarityService } from "src/app/services/clarity.service";
import { StoreRepositoryService } from "src/app/pages/store/store-repository.service";

@Injectable()
export class VoucherRedemptionService {
  isVoucherIsInvalid$ = new BehaviorSubject<boolean | string>(null);
  isVoucherCodeInvalid = false;
  isButtonDisabled = true;
  spinnerAnimation = false;
  progressBar = 0;
  proceed = true;
  claimSuccess = false;
  claimFromList = false;
  timer$: Observable<number> | null = null;
  sub: Subscription;
  callFromPromotion = "";

  private destroy$ = new Subject<void>();
  private claimedFromListSubject = new Subject<string>();
  readonly voucherClaimedFromList$ = this.claimedFromListSubject.asObservable();
  isVoucherDisableSignal = signal<boolean>(false);
  private storeRepository = inject(StoreRepositoryService);

  constructor(
    private voucherService: VoucherService,
    private authService: AuthService,
    private homeCardsService: HomecardsService,
    private router: Router,
    private clarityService: ClarityService,
  ) {}

  cleanInputdata(inputString: string | object): object {
    inputString = inputString ? inputString : {};
    const isEmpty = (val: string | object) => val === null || !(Object.keys(val as object) || (val as string).length);
    if (isEmpty(inputString)) return {};
    try {
      return JSON.parse(inputString as string);
    } catch (e) {
      return typeof inputString === "string" ? {} : inputString;
    }
  }

  handleDiscountVoucherRedirection(voucherData: Data): void {
    const buttonLink = voucherData?.button_link;
    if (!buttonLink) return;
    // Wallet Use deep-links to /store/products/... and skips store.openProduct, which normally
    // sets discount_available. Without this flag, checkout never loads Available vouchers.
    try {
      localStorage.setItem("isDiscount", "true");
    } catch {
      // Ignore storage failures; signal below still enables checkout voucher fetch.
    }
    this.storeRepository.iSdiscountAvailable.set(true);
    if (buttonLink.includes("products") || buttonLink.includes("tip")) {
      this.router.navigateByUrl(buttonLink); // explicit route from payload
    } else {
      this.router.navigateByUrl("mainTabs/tabs/store");
    }
  }

  startSpinner(): void {
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(20);

    try {
      this.sub = this.timer$?.subscribe(
        (sec) => {
          this.progressBar = sec;
        },
        (err) => {
          this.authService.errorHandlerLogs("[VoucherRedemptionService.startSpinner]", err);
        }
      );
    } catch (e) {
      this.authService.errorHandlerLogs("[VoucherRedemptionService.startSpinner] exception", e);
    }
  }

  getButtonStatus(voucherCode: string): void {
    this.isButtonDisabled = voucherCode === "";
    if (voucherCode === "") {
      this.isVoucherIsInvalid$.next("Voucher code is empty");
    } else {
      this.isVoucherIsInvalid$.next(false);
    }
  }

  redeemVoucher(voucherCode: string, claimFromList: boolean, voucherObj?: Data): void {
    this.isVoucherDisableSignal.set(true);
    this.clarityService.event("redeem_voucher");
    voucherObj = this.cleanInputdata(voucherObj);

    if (voucherObj?.discount_voucher) {
      this.handleDiscountVoucherRedirection(voucherObj);
      return;
    }

    this.isVoucherIsInvalid$.next(false);
    this.claimFromList = claimFromList;
    this.claimSuccess = false;

    if (this.claimFromList && voucherObj) {
      voucherObj.isClaiming = true;
    }

    try {
      this.voucherService
        .redeemVoucher(voucherCode, "/api_post_request/validate_voucher", this.callFromPromotion, "")
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (voucherResponse: VoucherClaimResult) => {
            this.progressBar = 100;
            this.sub?.unsubscribe();
            this.proceed = true;
            this.spinnerAnimation = false;
            this.isVoucherDisableSignal.set(false);

            if (this.claimFromList && voucherObj) {
              voucherObj.isClaiming = false;
            }

            if (voucherResponse?.status?.toLowerCase() === "success") {
              this.homeCardsService.updateAddToHomeCardStatus({
                event: "redeem_voucher",
                status: true,
              });

              this.voucherService.voucherRedeemed(voucherObj?.id).subscribe();
              this.claimSuccess = true;
              this.isVoucherIsInvalid$.next(false);

              if (claimFromList && voucherObj?.id) {
                this.claimedFromListSubject.next(voucherObj.id);
              }

              if (voucherResponse?.mobile_app_award_type === "offer") {
                this.voucherService.setSyndicatePending();
              }

              if (claimFromList) {
                this.authService
                  .getConstantsMsg("1079")
                  .then((translatedMsg) => {
                    const defaultMsg = {
                      message: { line_1: "Voucher claimed successfully", line_2: "" },
                      style: { icon: "tick circle success.png", type: "Success" },
                    };
                    this.authService?.showToastMessage(translatedMsg, defaultMsg);
                  })
                  .catch((error) => {
                    this.authService.errorHandlerLogs("[VoucherRedemptionService.redeemVoucher] getConstantsMsg fallback", error);
                  });
              }

              if (voucherResponse && typeof voucherResponse === "object" && voucherResponse.Voucher_description !== undefined) {
                this.voucherService.onVoucherSuccessClaim(voucherResponse);
              } else {
                this.authService.errorHandlerLogs("[VoucherRedemptionService.redeemVoucher] Invalid voucher response structure:", voucherResponse);
                this.authService.getConstantsMsg("1026").then((translatedMsg) => {
                  const defaultMsg = {
                    message: { line_1: "Voucher claimed but missing details", line_2: "" },
                    style: { icon: "tick circle error.svg", type: "Error" },
                  };
                  this.authService.showToastMessage(translatedMsg, defaultMsg);
                });
              }
              return;
            }

            if (voucherResponse?.status?.toLowerCase() === "rejected") {
              this.authService
                .getConstantsMsg(voucherResponse?.error_code)
                .then((translatedMsg) => {
                  const defaultMsg = {
                    message: { line_1: "Something went wrong!", line_2: "" },
                    style: { icon: "tick circle error.svg", type: "Error" },
                  };
                  this.authService.showToastMessage(translatedMsg, defaultMsg);
                })
                .catch((err) => this.authService.errorHandlerLogs("[VoucherRedemptionService.redeemVoucher] rejected message", err));

              this.proceed = true;
              this.claimFromList = false;
              this.isVoucherIsInvalid$.next(voucherResponse.message);
              return;
            }

            this.proceed = true;
            this.spinnerAnimation = false;

            if (this.claimFromList && voucherObj) {
              voucherObj.isClaiming = false;
            }

            if (
              voucherResponse?.message?.toLowerCase().includes("no longer") ||
              (voucherResponse?.status?.toLowerCase() === "fail" &&
                (voucherResponse?.message?.toLowerCase().includes("expired") ||
                  voucherResponse?.message?.toLowerCase().includes("invalid")))
            ) {
              this.voucherService?.hideInvalidVoucher(voucherObj?.id).subscribe();
            }

            if (voucherResponse?.message && claimFromList) {
              const translatedMsg = {
                message: { line_1: voucherResponse?.message, line_2: "" },
                style: { icon: "tick circle error.svg", type: "Error" },
              };
              const defaultMsg = {
                message: { line_1: "Something went wrong!", line_2: "" },
                style: { icon: "tick circle error.svg", type: "Error" },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            } else {
              this.isVoucherIsInvalid$.next(voucherResponse?.message ? voucherResponse.message : null);
            }

            this.claimFromList = false;
          },
          (error) => {
            this.authService.errorHandlerLogs("[VoucherRedemptionService.redeemVoucher] Failed to redeem voucher:", error);
            this.claimFromList = false;
            if (this.claimFromList && voucherObj) {
              voucherObj.isClaiming = false;
            }
            this.claimFromList = false;

            this.sub?.unsubscribe();
            this.proceed = true;
            this.spinnerAnimation = false;

            this.authService.getConstantsMsg("1026").then((translatedMsg) => {
              const defaultMsg = {
                message: { line_1: "Something went wrong. Please try again", line_2: "" },
                style: { icon: "tick circle error.svg", type: "Error" },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            });
          }
        );
    } catch (e) {
      this.authService.errorHandlerLogs("[VoucherRedemptionService.redeemVoucher] Exception caught:", e);
    }
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.claimedFromListSubject.complete();
    this.sub?.unsubscribe();
    this.timer$ = null;
  }
}
